import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import {
  Appointment,
  AppointmentV2,
  BookingResponse,
  OutputAppointmentTypePublic,
  AvailabilitySlot,
} from '@dialogue/timekeeper'
import {
  GetAvailabilitiesParams,
  GetProviderAvailabilitiesParams,
  ProviderBookingState,
  ProviderAttributes,
  Provider,
  UpdateBookingParams,
  ProviderDetailsData,
} from 'APP/Store/ProviderBooking/types'
import { CreateAppointmentPayload } from 'APP/Store/AppointmentBooking/types'

const initialState: ProviderBookingState = {
  selectedDay: null,
  availabilities: {
    data: [],
    loading: false,
    error: null,
  },
  providerAvailabilities: {},
  createAppointmentRequest: {
    data: null,
    loading: false,
    error: null,
  },
  providers: {
    data: {},
    loadingList: false,
    loadingBio: false,
    error: null,
  },
  attributes: {
    loading: false,
    error: null,
    data: null,
  },
  //providerDetails is used to manage the information of a specific provider.
  providerDetails: {
    loading: false,
    error: null,
    data: null,
  },
  appointmentTypes: {
    data: [],
    loading: false,
    fetched: false,
    error: null,
  },
  updateAppointmentRequest: {
    data: null,
    loading: false,
    error: null,
  },
  booking: {
    data: null,
    loading: false,
    error: null,
  },
  selectedProviderId: null,
}

const { actions, reducer } = createSlice({
  name: 'providerBooking',
  initialState,
  reducers: {
    selectDay: (state, action: PayloadAction<string>) => {
      // Selecting another day starts its search, so the slots held until it lands are stale.
      if (state.selectedDay !== action.payload) {
        state.availabilities.loading = true
      }
      state.selectedDay = action.payload
    },
    getAvailabilities: (state, _action: PayloadAction<GetAvailabilitiesParams>) => {
      state.availabilities.error = null
      state.availabilities.loading = true
    },
    getAvailabilitiesSuccess: (
      state,
      action: PayloadAction<{
        availabilities: AvailabilitySlot[]
      }>
    ) => {
      state.availabilities.loading = false
      state.availabilities.data = action.payload.availabilities
    },
    getAvailabilitiesFailure: (state, action: PayloadAction<{ error: Error }>) => {
      state.availabilities.loading = false
      state.availabilities.error = action.payload.error
    },
    getBooking: (state, _action: PayloadAction<string>) => {
      state.booking.loading = true
      state.booking.error = null
      state.booking.data = null
    },
    getBookingSuccess: (state, action: PayloadAction<{ booking: BookingResponse }>) => {
      state.booking.loading = false
      state.booking.data = action.payload.booking
      state.booking.error = null
    },
    getBookingFailure: (state, action: PayloadAction<{ error: Error }>) => {
      state.booking.loading = false
      state.booking.error = action.payload.error
    },
    createAppointment: (state, _: PayloadAction<CreateAppointmentPayload>) => {
      state.createAppointmentRequest.loading = true
    },
    createAppointmentSuccess: (state, action: PayloadAction<Appointment | AppointmentV2>) => {
      state.createAppointmentRequest.loading = false
      state.createAppointmentRequest.data = action.payload
    },
    createAppointmentFailure: (state, action: PayloadAction<{ error: Error }>) => {
      state.createAppointmentRequest.loading = false
      state.createAppointmentRequest.error = action.payload.error
    },
    resetAppointment: (state) => {
      state.createAppointmentRequest = {
        data: null,
        loading: false,
        error: null,
      }
      /**
       * We intentionally do not reset selectedProviderId here, as it is needed to maintain
       * the context of the user's selection when retrying a booking after a failure.
       */
    },
    resetState: () => initialState,
    getProviders: (state, _: PayloadAction<number[]>) => {
      state.providers.loadingList = true
    },
    getProvidersSuccess: (state) => {
      state.providers.loadingList = false
      state.providers.error = null
    },
    getProvidersFailure: (state, action: PayloadAction<{ error: Error }>) => {
      state.providers.loadingList = false
      state.providers.error = action.payload.error
    },
    getProviderSuccess: (state, action: PayloadAction<Provider>) => {
      const existing = state.providers.data[action.payload.id] || {}
      state.providers.data[action.payload.id] = { ...existing, ...action.payload }
    },
    getProviderBio: (state, _: PayloadAction<number>) => {
      state.providers.loadingBio = true
    },
    getProviderBioSuccess: (
      state,
      action: PayloadAction<{ id: number; bio?: string | null; jobTitle?: string }>
    ) => {
      const providerData = state.providers.data[action.payload.id] || {}
      state.providers.loadingBio = false
      state.providers.data[action.payload.id] = {
        ...providerData,
        ...action.payload,
      }
    },
    getProviderBioFailure: (state, action: PayloadAction<{ id: number }>) => {
      state.providers.loadingBio = false
      const existing = state.providers.data[action.payload.id] || {}
      state.providers.data[action.payload.id] = { ...existing, bio: null }
    },
    getProvidersAttributes: (state) => {
      state.attributes.loading = true
    },
    getProvidersAttributesSuccess: (
      state,
      action: PayloadAction<{ attributes: ProviderAttributes }>
    ) => {
      state.attributes.loading = false
      state.attributes.data = action.payload.attributes
      state.attributes.error = null
    },
    getProvidersAttributesFailure: (state, action: PayloadAction<{ error: Error }>) => {
      state.attributes.loading = false
      state.attributes.error = action.payload.error
    },
    getProviderById: (state, _: PayloadAction<number>) => {
      state.providerDetails.loading = true
    },
    getProviderByIdSuccess: (
      state,
      action: PayloadAction<{ providerData: ProviderDetailsData }>
    ) => {
      state.providerDetails.loading = false
      state.providerDetails.data = action.payload.providerData
      state.providerDetails.error = null
    },
    getProviderByIdFailure: (state, action: PayloadAction<{ error: Error }>) => {
      state.providerDetails.loading = false
      state.providerDetails.error = action.payload.error
    },
    getAppointmentTypes: (state) => {
      state.appointmentTypes.loading = true
    },
    getAppointmentTypesSuccess: (state, action: PayloadAction<OutputAppointmentTypePublic[]>) => {
      state.appointmentTypes.loading = false
      state.appointmentTypes.fetched = true
      state.appointmentTypes.data = action.payload
    },
    getAppointmentTypesFailure: (state, action: PayloadAction<{ error: Error }>) => {
      state.appointmentTypes.loading = false
      state.appointmentTypes.fetched = true
      state.appointmentTypes.error = action.payload.error
    },
    updateAppointmentSuccess: (state, action: PayloadAction<Appointment>) => {
      state.updateAppointmentRequest.loading = false
      state.updateAppointmentRequest.data = action.payload
      state.updateAppointmentRequest.error = null
    },
    updateAppointmentFailure: (state, action: PayloadAction<{ error: Error }>) => {
      state.updateAppointmentRequest.loading = false
      state.updateAppointmentRequest.error = action.payload.error
      state.updateAppointmentRequest.data = null
    },
    updateBooking: (state, _: PayloadAction<UpdateBookingParams>) => {
      // Persisting preferences is a background refresh of the existing booking, so we do
      // not toggle booking.loading (that gates rendering and is owned by the initial fetch).
      state.booking.error = null
    },
    updateBookingSuccess: (state, action: PayloadAction<{ booking: BookingResponse }>) => {
      state.booking.data = action.payload.booking
      state.booking.error = null
    },
    updateBookingFailure: (state, action: PayloadAction<{ error: Error }>) => {
      state.booking.error = action.payload.error
    },
    getProviderAvailabilities: (state, action: PayloadAction<GetProviderAvailabilitiesParams>) => {
      const { providerId } = action.payload

      const getExistingAvailabilitiesFromDay = () =>
        state.availabilities.data.filter((availability) => availability.provider_id === providerId)

      const previousProviderAvailabilities = state.providerAvailabilities[providerId]?.data ?? []

      /**
       * While fetching new availabilities for the provider,
       * we keep showing any existing ones.
       */
      const existingAvailabilities = previousProviderAvailabilities.length
        ? previousProviderAvailabilities
        : getExistingAvailabilitiesFromDay()

      state.providerAvailabilities[providerId] = {
        data: existingAvailabilities,
        loading: !existingAvailabilities.length,
        error: null,
      }
    },
    getProviderAvailabilitiesSuccess: (
      state,
      action: PayloadAction<{ providerId: number; availabilities: AvailabilitySlot[] }>
    ) => {
      const { providerId } = action.payload

      if (!state.providerAvailabilities[providerId]) return

      state.providerAvailabilities[providerId].data = action.payload.availabilities
      state.providerAvailabilities[providerId].loading = false
    },
    getProviderAvailabilitiesFailure: (
      state,
      action: PayloadAction<{ providerId: number; error: Error }>
    ) => {
      const { providerId } = action.payload

      if (!state.providerAvailabilities[providerId]) return

      state.providerAvailabilities[providerId].loading = false
      state.providerAvailabilities[providerId].error = action.payload.error
    },
    getProviderAvailabilitiesCancelled: (state, action: PayloadAction<{ providerId: number }>) => {
      const { providerId } = action.payload

      if (!state.providerAvailabilities[providerId]) return

      state.providerAvailabilities[providerId].loading = false
    },
    setSelectedProviderId: (state, action: PayloadAction<number | null>) => {
      state.selectedProviderId = action.payload
    },
  },
})

export { actions as providerBookingActions, initialState }

export default reducer
